/**
 * Default values for scouting filter ranges
 *
 * Defines min/max bounds and step increments for all range filters
 * in the filter sheet. Used to initialize filter state and configure
 * MultiSlider components.
 * @module features/scouting/constants/filterDefaults
 */

/** Default age range for player age filter */
export const DEFAULT_AGE_RANGE: [number, number] = [16, 40];

/** Default contract months remaining range */
export const DEFAULT_CONTRACT_MONTHS_RANGE: [number, number] = [0, 60];

/** Default player valuation range (in base currency units) */
export const DEFAULT_VALUATION_RANGE: [number, number] = [0, 200_000_000];

/** Default range for 0-100 score metrics (GPR, fit scores, performance) */
export const DEFAULT_SCORE_RANGE: [number, number] = [0, 100];

/** Default range for 0-1 decimal score metrics (fitScore stored as decimal in DB) */
export const DEFAULT_SCORE_RANGE_DECIMAL: [number, number] = [0, 1];

/** Default range for custom stat filters (org-level metrics use 0-100 scale) */
export const DEFAULT_CUSTOM_STAT_RANGE: [number, number] = [0, 100];

/** All range filter keys that the backend query supports */
export const SUPPORTED_RANGE_FILTER_KEYS = [
  "age",
  "geminiPlayerValuation",
  "contractMonths",
  "gpr",
  "fitScore",
  "teamStyleFit",
  "carrying",
  "passing",
  "dribbling",
  "shooting",
  "gkShotStopping",
  "gkPositioning",
  "gkPassing",
] as const;

/** Type for supported range filter keys */
export type RangeFilterKey = (typeof SUPPORTED_RANGE_FILTER_KEYS)[number];

/**
 * Checks if a range filter has been modified from its default values
 *
 * Used by the filter sheet to determine which chips should display
 * as "active" and whether the reset button should be enabled.
 * @param value - Current range tuple [min, max]
 * @param defaultRange - Default range tuple to compare against
 * @returns true if either bound differs from the default
 */
export const isRangeActive = (
  value: [number, number],
  defaultRange: [number, number]
): boolean => value[0] !== defaultRange[0] || value[1] !== defaultRange[1];
